import { fetchWithAuth } from "../utils/fetchWithAuth";

class AutonomousStrategyWorkflowService {
  constructor() {
    this.AUTONOMOUS_STEPS = [
      {
        id: "deep_analysis",
        title: "Deep Challenge Analysis",
        description: "Anaizing your unique challenge context and requirements",
        status: "pending",
        agentKey: "DESIGN_LEAD", // Design Lead
      },
      {
        id: "contextual_research",
        title: "Contextual Research Planning",
        description:
          "Creating research approaches tailored to your specific domain",
        status: "pending",
        agentKey: "UX_RESEARCH", // UX Research Lead
      },
      {
        id: "custom_programs",
        title: "Custom Program Generation",
        description:
          "Creating bespoke programs and activities for your challenge",
        status: "pending",
        agentKey: "UX_DESIGN_LEAD", // UX Design Lead
      },
      {
        id: "synthesis",
        title: "Strategy Synthesis",
        description:
          "Integrating all elements into a cohesive autonomous strategy",
        status: "pending",
        agentKey: "DESIGN_LEAD", // Design Lead
      },
    ];

    this.workflowResults = {};
    this.conversationHistory = [];
  }

  getSteps() {
    return [...this.AUTONOMOUS_STEPS];
  }

  async executeWorkflow(
    designChallenge,
    contextualRequirements,
    callbacks,
    csvData = null,
    executionPlan = null,
  ) {
    console.log("🔥🔥🔥 AUTONOMOUS LOG: executeWorkflow() STARTED", {
      designChallenge: designChallenge?.substring(0, 100) + '...',
      hasContextualRequirements: !!contextualRequirements,
      hasCsvData: !!csvData,
      callbacksProvided: Object.keys(callbacks),
      executionPlan: executionPlan ? `Modular (${executionPlan.steps.length} steps)` : 'Full (4 steps)'
    });

    // Store CSV data for tool filtering (same data source as regular workflow)
    if (csvData) {
      this.csvToolsCache = csvData.tools || [];
      this.csvProgramsCache = csvData.programsOfWork || [];
      this.csvMethodsCache = csvData.methods || [];
    }
    const { onStepStart, onStepComplete, onComplete, onError, onChatMessage, onInsufficientCredits } =
      callbacks;

    // Check credits before starting (strategy workflow = strategy-section cost)
    try {
      const creditCheck = await fetchWithAuth('/api/credits/check?action=strategy-section');
      if (creditCheck.ok) {
        const creditData = await creditCheck.json();
        if (!creditData.allowed) {
          if (onInsufficientCredits) onInsufficientCredits();
          else if (onError) onError(Object.assign(new Error('Insufficient credits'), { type: 'insufficient_credits' }));
          return null;
        }
      }
    } catch (_) {}

    console.log("🔥🔥🔥 AUTONOMOUS LOG: Callbacks destructured", {
      hasOnStepStart: !!onStepStart,
      hasOnStepComplete: !!onStepComplete,
      hasOnComplete: !!onComplete,
      hasOnError: !!onError,
      hasOnChatMessage: !!onChatMessage
    });

    try {
      const allSteps = this.getSteps();
      const skipSteps = executionPlan?.skipSteps || [];

      // Create filtered steps array with only required steps
      const activeSteps = allSteps.filter(step => !skipSteps.includes(step.id));
      const stepIndexMap = {};
      activeSteps.forEach((step, index) => {
        stepIndexMap[step.id] = index;
      });

      console.log("🎯 Execution Plan:", {
        requiredSteps: executionPlan?.steps || ['all'],
        skipSteps: skipSteps,
        isQuickMode: executionPlan?.isQuickPlan,
        activeStepsCount: activeSteps.length,
        stepMapping: stepIndexMap
      });

      // Step 1: Deep Analysis - AI-driven challenge understanding
      let deepAnalysisResults;
      if (!skipSteps.includes('deep_analysis')) {
        const stepIndex = stepIndexMap['deep_analysis'];
        if (onStepStart) onStepStart(stepIndex, { ...activeSteps[stepIndex], agent: "Product Lead" });

        deepAnalysisResults = await this.executeDeepAnalysis(designChallenge);
        console.log("📝 Deep Analysis completed by Design Lead");
        if (onStepComplete)
          onStepComplete(
            stepIndex,
            { ...activeSteps[stepIndex], status: "completed" },
            deepAnalysisResults,
          );
      } else {
        console.log("⏭️ Skipping Deep Analysis - not required for this workflow");
        deepAnalysisResults = { skipped: true };
      }

      // Step 2: Contextual Research - UX Research creates tailored research approach
      let researchResults;
      if (!skipSteps.includes('contextual_research')) {
        const stepIndex = stepIndexMap['contextual_research'];
        if (onStepStart)
          onStepStart(stepIndex, { ...activeSteps[stepIndex], agent: "UX Research Lead" });

        researchResults = await this.executeContextualResearch(
          designChallenge,
          deepAnalysisResults,
        );
        console.log("📝 Contextual Research completed by UX Research Lead");
        if (onStepComplete)
          onStepComplete(
            stepIndex,
            { ...activeSteps[stepIndex], status: "completed" },
            researchResults,
          );
      } else {
        console.log("⏭️ Skipping Contextual Research - not required for this workflow");
        researchResults = { skipped: true };
      }

      // Step 3: Custom Programs - UX Design creates bespoke programs
      let programsResults;
      if (!skipSteps.includes('custom_programs')) {
        const stepIndex = stepIndexMap['custom_programs'];
        if (onStepStart) onStepStart(stepIndex, { ...activeSteps[stepIndex], agent: "UX Design Lead" });

        programsResults = await this.executeCustomProgramGeneration(
          designChallenge,
          deepAnalysisResults,
          researchResults,
        );
        console.log("📝 Custom Programs completed by UX Design Lead");
        if (onStepComplete)
          onStepComplete(
            stepIndex,
            { ...activeSteps[stepIndex], status: "completed" },
            programsResults,
          );
      } else {
        console.log("⏭️ Skipping Custom Programs - not required for this workflow");
        programsResults = { skipped: true };
        
        // Set minimal placeholder for workflows that skip program generation (e.g., Decision Call)
        this.workflowResults.customPrograms = {
          skipped: true,
          autonomousPrograms: [],
          interconnectedTools: []
        };
        this.workflowResults.processedPrograms = [];
      }

      // Step 4: Synthesis - Design Lead integrates everything
      let synthesisResults;
      if (!skipSteps.includes('synthesis')) {
        const stepIndex = stepIndexMap['synthesis'];
        if (onStepStart) onStepStart(stepIndex, { ...activeSteps[stepIndex], agent: "Product Lead" });

        synthesisResults = await this.executeSynthesis();
        console.log("📝 Synthesis completed by Design Lead");
        if (onStepComplete)
          onStepComplete(
            stepIndex,
            { ...activeSteps[stepIndex], status: "completed" },
            synthesisResults,
          );
      } else {
        console.log("⏭️ Skipping Synthesis - not required for this workflow");
        synthesisResults = this.workflowResults;
      }

      // Add metadata about execution plan for downstream handlers
      const finalResults = {
        ...synthesisResults,
        workflowMetadata: {
          executionPlan: {
            requiredSteps: executionPlan?.steps || activeSteps.map(s => s.id),
            skipSteps: skipSteps,
            activeStepsCount: activeSteps.length,
            isQuickPlan: executionPlan?.isQuickPlan || false,
            customProgramsSkipped: skipSteps.includes('custom_programs')
          },
          completedAt: new Date().toISOString()
        }
      };

      // The compilation is already done in executeSynthesis, just return the results
      console.log("🔥🔥🔥 WORKFLOW SERVICE: Ready to call onComplete", {
        hasOnComplete: !!onComplete,
        synthesisResultsKeys: Object.keys(finalResults || {}),
        skipSteps: skipSteps,
        customProgramsSkipped: skipSteps.includes('custom_programs')
      });
      
      if (onComplete) {
        console.log("🔥🔥🔥 WORKFLOW SERVICE: Calling onComplete NOW with metadata");
        onComplete(finalResults);
      } else {
        console.error("❌❌❌ WORKFLOW SERVICE: onComplete callback is NULL - completion chain will NOT fire!");
      }

      // Deduct credits after successful completion (strategy-section = 2 credits)
      try {
        const deductRes = await fetchWithAuth('/api/credits/deduct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'strategy-section' }),
        });
        if (deductRes.ok) {
          const deductData = await deductRes.json();
          if (!deductData.success) {
            // Deduction failed (e.g. race depleted remaining balance). Surface top-up prompt.
            if (onInsufficientCredits) onInsufficientCredits();
          }
        }
      } catch (_) {}

      return finalResults;
    } catch (error) {
      console.error("Autonomous workflow execution failed:", error);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  async executeDeepAnalysis(designChallenge) {
    try {
      // AI-driven deep analysis that goes beyond template matching
      const result = await fetchWithAuth("/autonomous-ai/autonomous-analysis", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          analysis_type: "deep_contextual",
          use_csv_as_knowledge: false, // Deep analysis doesn't need CSV knowledge
          agentId: "DESIGN_LEAD", // Design Lead
          context: { source: "autonomous_workflow", step: "deep_analysis" },
        },
      });

      if (!result.ok) {
        throw new Error(`Deep analysis failed with status ${result.status}`);
      }

      const analysisData = await result.json();

      // Store comprehensive analysis results
      this.workflowResults.deepAnalysis = {
        ...analysisData,
        contextualFactors: analysisData.contextual_factors || [],
        domainSpecificInsights: analysisData.domain_insights || [],
        uniqueConstraints: analysisData.unique_constraints || [],
        opportunityAreas: analysisData.opportunity_areas || [],
      };

      return this.workflowResults.deepAnalysis;
    } catch (error) {
      console.error("Deep analysis step failed:", error);
      throw error;
    }
  }

  async executeContextualResearch(designChallenge, deepAnalysis) {
    try {
      // UX Research creates contextual research approaches
      const result = await fetchWithAuth(
        "/autonomous-ai/autonomous-research-planning",
        {
          method: "POST",
          body: {
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            research_type: "contextual_adaptive",
            use_csv_as_knowledge: false, // Research planning doesn't need CSV knowledge
            agentId: "UX_RESEARCH", // UX Research Lead
            context: {
              source: "autonomous_workflow",
              step: "contextual_research",
            },
          },
        },
      );

      if (!result.ok) {
        throw new Error(
          `Contextual research failed with status ${result.status}`,
        );
      }

      const researchData = await result.json();

      this.workflowResults.contextualResearch = {
        ...researchData,
        researchMethods: researchData.research_methods || [],
        dataCollectionApproaches: researchData.data_collection || [],
        validationStrategies: researchData.validation_strategies || [],
      };

      return this.workflowResults.contextualResearch;
    } catch (error) {
      console.error("Contextual research step failed:", error);
      throw error;
    }
  }

  async executeCustomProgramGeneration(
    designChallenge,
    deepAnalysis,
    researchPlan,
  ) {
    try {
      // UX Design creates completely custom programs
      const result = await fetchWithAuth(
        "/autonomous-ai/autonomous-program-generation",
        {
          method: "POST",
          body: {
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            research_plan: researchPlan,
            generation_type: "fully_custom",
            csv_knowledge_base: true, // Use CSV as reference, not constraint
            csvData: {
              tools: this.csvToolsCache,
              methods: this.csvMethodsCache,
              programsOfWork: this.csvProgramsCache,
            },
            agentId: "UX_DESIGN_LEAD", // UX Design Lead
            context: { source: "autonomous_workflow", step: "custom_programs" },
          },
        },
      );

      if (!result.ok) {
        throw new Error(
          `Custom program generation failed with status ${result.status}`,
        );
      }

      const programsData = await result.json();

      this.workflowResults.customPrograms = {
        ...programsData,
        autonomousPrograms: programsData.autonomous_programs || [],
        interconnectedTools: programsData.interconnected_tools || [], // Keep AI tools for reference
        adaptiveMethodologies: programsData.adaptive_methodologies || [],
        innovativeApproaches: programsData.innovative_approaches || [],
      };

      // MOVE HEAVY PROCESSING HERE - do it during step 3, not step 4
      console.log("🔧 Processing programs with tool correlation and AI enhancement...");
      await this.processAndEnhancePrograms();

      return this.workflowResults.customPrograms;
    } catch (error) {
      console.error("Custom program generation step failed:", error);
      throw error;
    }
  }



  async executeSynthesis() {
    try {
      // Synthesis is now just final compilation - heavy processing was done in step 3
      console.log("🎯 Final synthesis: compiling pre-processed autonomous strategy...");

      // Validate we have the necessary data before compilation
      if (!this.workflowResults.deepAnalysis) {
        throw new Error("Missing deep analysis results for synthesis");
      }

      // Custom programs are optional for lightweight workflows (e.g., Decision Call)
      if (!this.workflowResults.customPrograms) {
        console.warn("⚠️ No custom programs - using strategy-only compilation");
        this.workflowResults.customPrograms = {
          skipped: true,
          autonomousPrograms: [],
          interconnectedTools: []
        };
        this.workflowResults.processedPrograms = [];
      }

      const compiledResults = await this.compileFinalResults();

      // Validate the compiled results have essential data
      if (!compiledResults.finalProgramSequence || compiledResults.finalProgramSequence.length === 0) {
        console.warn("⚠️ No programs in final sequence - this may cause UI issues");
      }

      this.workflowResults.synthesis = {
        complete: true,
        timestamp: new Date().toISOString(),
        autonomousStrategy: compiledResults,
      };

      console.log("✅ Synthesis completed successfully");
      return compiledResults;
    } catch (error) {
      console.error("❌ Synthesis step failed:", error);
      console.error("Workflow results state:", {
        hasDeepAnalysis: !!this.workflowResults.deepAnalysis,
        hasContextualResearch: !!this.workflowResults.contextualResearch,
        hasCustomPrograms: !!this.workflowResults.customPrograms,
        hasProcessedPrograms: !!this.workflowResults.processedPrograms,
      });
      throw error;
    }
  }

  // NEW METHOD: Handle heavy processing during step 3
  async processAndEnhancePrograms() {
    console.log("🔧 Starting heavy program processing and AI enhancement...");

    if (!this.workflowResults.customPrograms) {
      throw new Error("No custom programs to process");
    }

    const autonomousPrograms = this.workflowResults.customPrograms.autonomousPrograms || [];
    const interconnectedTools = this.workflowResults.customPrograms.interconnectedTools || [];

    // Deduplicate tools globally before processing programs - STRICT DEDUPLICATION
    const allToolsGlobal = new Set();
    const toolStageMap = new Map(); // Track which stage each tool was first assigned to
    const cleanedInterconnectedTools = interconnectedTools.filter(tool => {
      const normalizedName = tool.tool.toLowerCase().trim();
      const toolKey = `${normalizedName}_${tool.stage_assignment}`;

      if (allToolsGlobal.has(normalizedName)) {
        console.log(`🧹 Removed duplicate tool globally: "${tool.tool}" (already assigned to ${toolStageMap.get(normalizedName)})`);
        return false;
      }

      allToolsGlobal.add(normalizedName);
      toolStageMap.set(normalizedName, tool.stage_assignment);
      return true;
    });

    console.log(`🧹 STRICT tool deduplication: ${interconnectedTools.length} → ${cleanedInterconnectedTools.length} tools`);

    // Process programs with correlation logic and AI enhancement
    const processedPrograms = await Promise.all(autonomousPrograms.map(async (program, index) => {
      console.log(`🔄 Processing program ${index + 1}/${autonomousPrograms.length}: ${program.program_of_work}`);

      const stages = program.custom_stages || program.stages || ["Implementation"];

      const toolsByStage = this.createToolsByStageFromCustomProgram(
        program,
        cleanedInterconnectedTools,
        stages,
      );

      const calculatedDuration = Object.values(toolsByStage).reduce(
        (total, stage) => total + (parseInt(stage.totalDuration, 10) || 0),
        0,
      );
      const finalDuration = calculatedDuration > 0 ? calculatedDuration : parseInt(program.duration_estimate, 10) || 40;

      const deliverables = program.deliverables_feed_forward
        ? Array.isArray(program.deliverables_feed_forward)
          ? program.deliverables_feed_forward
          : program.deliverables_feed_forward.split(",").map((d) => d.trim())
        : [];

      const programDeliverables = program.program_deliverables 
        ? Array.isArray(program.program_deliverables)
          ? program.program_deliverables
          : program.program_deliverables.split(",").map((d) => d.trim())
        : deliverables;

      const programStakeholders = program.program_stakeholders
        ? Array.isArray(program.program_stakeholders) 
          ? program.program_stakeholders
          : program.program_stakeholders.split(",").map((s) => s.trim())
        : [];

      let finalDeliverables = programDeliverables.length > 0 ? programDeliverables : deliverables;
      let finalStakeholders = programStakeholders;

      // Generate missing deliverables and stakeholders using AI
      if (finalDeliverables.length === 0 || finalStakeholders.length === 0) {
        try {
          const designChallenge = this.workflowResults.deepAnalysis?.problem_statement || 
                                this.workflowResults.deepAnalysis?.short_summary || 
                                "Design challenge";

          if (finalDeliverables.length === 0) {
            const deliverablesResponse = await this.generateProgramDeliverables(
              designChallenge, 
              program, 
              toolsByStage
            );
            if (deliverablesResponse && deliverablesResponse.length > 0) {
              finalDeliverables = deliverablesResponse;
            }
          }

          if (finalStakeholders.length === 0) {
            const stakeholdersResponse = await this.generateProgramStakeholders(
              designChallenge,
              program,
              toolsByStage
            );
            if (stakeholdersResponse && stakeholdersResponse.length > 0) {
              finalStakeholders = stakeholdersResponse;
            }
          }
        } catch (error) {
          console.warn(`⚠️ Failed to generate missing deliverables/stakeholders for ${program.program_of_work}:`, error);
        }
      }

      let programMethods;
      try {
          programMethods = this.extractMethodsFromProgram(program);
      } catch (methodError) {
          console.error(`❌ CRITICAL: AI failed to provide methods for ${program.program_of_work}:`, methodError);
          console.error(`❌ This indicates the autonomous AI prompt needs fixing - the AI should ALWAYS provide methodology`);
          // DO NOT USE FALLBACKS - throw error to surface the real problem
          throw new Error(`Autonomous AI failed to provide methodology for program: ${program.program_of_work}. Fix the AI prompt.`);
      }

      return {
        program_of_work: program.program_of_work,
        phase: program.phase,
        goal: program.goal || program.description,
        description: program.description,
        totalProgramLength: finalDuration,
        isIncluded: true,
        methods: programMethods,
        Stages: stages,
        focus: [],
        expectedDeliverables: finalDeliverables,
        stakeholdersInvolvement: finalStakeholders.length > 0 ? finalStakeholders.join("\n") : "",
        toolsByStage: toolsByStage,
        score: 0,
        similarity_score: 0,
        programType: program.program_type || "Strategic Phase",
        prerequisites: program.prerequisites || [],
        deliverablesFeedForward: program.deliverables_feed_forward || [],
        sequencePosition: index + 1,
        isAutonomous: true,
      };
    }));

    // Store processed programs for fast retrieval in synthesis
    this.workflowResults.processedPrograms = processedPrograms;

    console.log(`✅ Completed heavy processing of ${processedPrograms.length} programs`);
  }

  // NEW METHOD: Fast final compilation using pre-processed data
  async compileFinalResults() {
    console.log("🎯 Starting final compilation of autonomous results...");

    const results = {};

    // Extract from deep analysis
    if (this.workflowResults.deepAnalysis) {
      console.log("📊 Extracting deep analysis data...");
      results.planName = this.workflowResults.deepAnalysis.plan_name;
      results.shortSummary = this.workflowResults.deepAnalysis.short_summary;
      results.problemStatement =
        this.workflowResults.deepAnalysis.problem_statement;
      results.goalStatement = this.workflowResults.deepAnalysis.goal_statement;
      results.targetStatement =
        this.workflowResults.deepAnalysis.target_statement;
      results.contextualFactors =
        this.workflowResults.deepAnalysis.contextualFactors;
      results.domainInsights =
        this.workflowResults.deepAnalysis.domainSpecificInsights;

      // CRITICAL: Extract all components from autonomous analysis
      results.objectives = Array.isArray(this.workflowResults.deepAnalysis.objectives_statement)
        ? this.workflowResults.deepAnalysis.objectives_statement
        : this.workflowResults.deepAnalysis.opportunity_areas || [];

      results.deliverables = Array.isArray(this.workflowResults.deepAnalysis.deliverables_statement)
        ? this.workflowResults.deepAnalysis.deliverables_statement
        : [];

      results.metrics = Array.isArray(this.workflowResults.deepAnalysis.metrics_statement)
        ? this.workflowResults.deepAnalysis.metrics_statement
        : [];

      results.stakeholders = Array.isArray(this.workflowResults.deepAnalysis.stakeholders_involvement)
        ? this.workflowResults.deepAnalysis.stakeholders_involvement
        : [];

      results.experienceDesignPrinciples = this.workflowResults.deepAnalysis.experience_design_principles || "";
    }

    // Extract from contextual research
    if (this.workflowResults.contextualResearch) {
      console.log("🔬 Extracting research data...");
      results.researchApproach =
        this.workflowResults.contextualResearch.researchMethods;
      results.dataCollection =
        this.workflowResults.contextualResearch.dataCollectionApproaches;
    }

    // Use pre-processed programs from step 3 - no heavy processing here
    if (this.workflowResults.processedPrograms) {
      console.log("🚀 Using pre-processed programs for fast compilation");

      const processedPrograms = this.workflowResults.processedPrograms;
      const interconnectedTools = this.workflowResults.customPrograms?.interconnectedTools || [];
      const sequenceStrategy = this.workflowResults.customPrograms?.program_sequence_strategy || "";

      results.finalProgramSequence = processedPrograms;
      results.selectedPrograms = processedPrograms;
      results.autonomousPrograms = this.workflowResults.customPrograms?.autonomousPrograms || [];
      results.interconnectedTools = interconnectedTools;
      results.programSequenceStrategy = sequenceStrategy;
      results.solutionMethodology = this.workflowResults.customPrograms?.solution_methodology || "";

      console.log(`✅ Compiled ${processedPrograms.length} programs successfully`);
    } else {
      console.warn("⚠️ No pre-processed programs found - this will cause issues");
      // If no processed programs, try to use the raw autonomous programs
      if (this.workflowResults.customPrograms?.autonomousPrograms) {
        console.log("🔄 Attempting fallback compilation from raw programs...");
        const autonomousPrograms = this.workflowResults.customPrograms.autonomousPrograms;

        // Basic conversion without heavy processing
        results.finalProgramSequence = autonomousPrograms.map((program, index) => ({
          program_of_work: program.program_of_work || program.name,
          phase: program.phase,
          goal: program.goal || program.description || program.focus,
          description: program.description || program.focus,
          totalProgramLength: program.duration_hours || parseInt(program.duration_estimate, 10) || 40,
          isIncluded: true,
          methods: program.methodology ? [program.methodology] : ["Adaptive Methodology"],
          Stages: program.stages || program.custom_stages || ["Implementation"],
          focus: [],
          expectedDeliverables: program.program_deliverables || program.deliverables_feed_forward || [],
          stakeholdersInvolvement: Array.isArray(program.program_stakeholders) 
            ? program.program_stakeholders.join("\n") 
            : (program.program_stakeholders || ""),
          toolsByStage: {},
          score: 0,
          similarity_score: 0,
          programType: program.program_type || "Strategic Phase",
          prerequisites: program.prerequisites || [],
          deliverablesFeedForward: program.deliverables_feed_forward || [],
          sequencePosition: index + 1,
          isAutonomous: true,
        }));

        results.selectedPrograms = results.finalProgramSequence;
        results.autonomousPrograms = autonomousPrograms;
        results.interconnectedTools = this.workflowResults.customPrograms?.interconnectedTools || [];

        console.log(`🔄 Fallback compilation completed with ${results.finalProgramSequence.length} programs`);
      } else {
        console.error("❌ No autonomous programs found at all - compilation will fail");
        results.finalProgramSequence = [];
        results.selectedPrograms = [];
      }
    }

    // Calculate total duration from programs
    const programsTotalDuration =
      results.finalProgramSequence?.reduce(
        (total, program) => total + (program.totalProgramLength || 0),
        0,
      ) || 0;
    results.totalDuration = programsTotalDuration || 120;

    // Validate that AI provided essential data - no fallbacks
    if (!results.objectives || results.objectives.length === 0) {
      throw new Error("AI failed to generate objectives. Please try again with a clearer design challenge.");
    }

    if (!results.deliverables || results.deliverables.length === 0) {
      throw new Error("AI failed to generate deliverables. Please try again with a clearer design challenge.");
    }

    if (!results.metrics || results.metrics.length === 0) {
      throw new Error("AI failed to generate metrics. Please try again with a clearer design challenge.");
    }

    if (!results.stakeholders || results.stakeholders.length === 0) {
      throw new Error("AI failed to generate stakeholders. Please try again with a clearer design challenge.");
    }

    if (!results.experienceDesignPrinciples) {
      throw new Error("AI failed to generate design principles. Please try again with a clearer design challenge.");
    }

    // Flag this as autonomous strategy
    results.isAutonomous = true;
    results.strategyType = "autonomous_contextual";
    results.createdBy = "AI Autonomous System";

    console.log("📤 Autonomous strategy compilation completed:", {
      hasAutonomousPrograms: !!results.autonomousPrograms,
      programCount: results.finalProgramSequence?.length || 0,
      hasInterconnectedTools: !!results.interconnectedTools,
      totalDuration: results.totalDuration,
      isAutonomous: results.isAutonomous,
    });

    return results;
  }

  // Helper method to create toolsByStage using AI-generated tools
  createToolsByStageFromCustomProgram(program, interconnectedTools, stages) {
    console.log(
      `🤖 Creating toolsByStage for program: ${program.program_of_work} using AI-generated tools`,
    );
    console.log(
      `🤖 Available interconnected tools:`,
      interconnectedTools.length,
    );
    console.log(`🤖 Program stages:`, stages);

    // USE AI-GENERATED TOOLS - This is the autonomous approach
    // Filter tools that belong to this specific program
    const programTools = interconnectedTools.filter((tool) => {
      const programMatch = tool.program_assignment === program.program_of_work;
      if (!programMatch) {
        console.log(
          `❌ Tool "${tool.tool}" program mismatch: "${tool.program_assignment}" !== "${program.program_of_work}"`,
        );
      }
      return programMatch;
    });

    console.log(
      `🤖 Found ${programTools.length} tools for program: ${program.program_of_work}`,
    );

    if (!programTools || programTools.length === 0) {
      console.warn(
        `⚠️ No AI-generated tools found for program: ${program.program_of_work}`,
      );
      console.warn(
        `⚠️ Available tools programs:`,
        interconnectedTools.map((t) => t.program_assignment),
      );
      // Return empty stages - DO NOT create fake fallback tools
      const emptyToolsByStage = {};
      stages.forEach((stage) => {
        emptyToolsByStage[stage] = { tools: [], totalDuration: 0, isAutonomous: true };
      });
      return emptyToolsByStage;
    }

    // Organize tools by stage with enhanced matching
    const toolsByStage = {};
    let totalToolsAssigned = 0;

    stages.forEach((stage) => {
      console.log(`🔍 Processing stage: "${stage}"`);

      // Find tools that belong to this stage with intelligent matching strategies
      const stageTools = programTools.filter((tool) => {
        // Exact match first (highest priority)
        if (tool.stage_assignment === stage) {
          console.log(
            `✅ Exact match found for "${tool.tool}" in stage "${stage}"`,
          );
          return true;
        }

        // Case-insensitive match (second priority)
        if (tool.stage_assignment?.toLowerCase() === stage.toLowerCase()) {
          console.log(
            `✅ Case-insensitive match found for "${tool.tool}" in stage "${stage}"`,
          );
          return true;
        }

        // Semantic similarity for autonomous tools (third priority)
        if (this.semanticStageMatch(tool.stage_assignment, stage)) {
          console.log(
            `✅ Semantic match found for "${tool.tool}": "${tool.stage_assignment}" → "${stage}"`,
          );
          return true;
        }

        // Fuzzy string match (fourth priority)
        if (this.fuzzyMatch(tool.stage_assignment, stage, 0.7)) {
          console.log(
            `✅ Fuzzy match found for "${tool.tool}": "${tool.stage_assignment}" ≈ "${stage}"`,
          );
          return true;
        }

        console.log(
          `❌ No match for "${tool.tool}": "${tool.stage_assignment}" vs "${stage}"`,
        );
        return false;
      });

      console.log(`🎯 Found ${stageTools.length} tools for stage "${stage}"`);
      totalToolsAssigned += stageTools.length;

      // Calculate total duration for this stage
      const totalDuration = stageTools.reduce(
        (sum, tool) => sum + (parseInt(tool.duration, 10) || 0),
        0,
      );

      toolsByStage[stage] = {
        tools: stageTools.map((tool, index) => ({
          ...tool,
          id: `ai-${Date.now()}-${index}-${tool.tool.toLowerCase().replace(/\s+/g, "-")}`,
          related_stage: stage,
          isAutonomous: true,
        })),
        totalDuration: totalDuration,
        isAutonomous: true,
      };

      // If no tools found for this stage, leave it empty - DO NOT create fake fallback tools
      if (stageTools.length === 0) {
        console.warn(
          `⚠️ No tools found for stage "${stage}" - stage will be empty (no fallback created)`,
        );
      }
    });

    console.log(
      `✅ AI-generated tools organized: ${Object.keys(toolsByStage).length} stages, ${totalToolsAssigned}/${programTools.length} tools assigned`,
    );

    // Log any unassigned tools for debugging
    const assignedToolIds = Object.values(toolsByStage).flatMap((stage) =>
      stage.tools.filter((t) => !t.isFallback).map((t) => t.tool),
    );
    const unassignedTools = programTools.filter(
      (tool) => !assignedToolIds.includes(tool.tool),
    );
    if (unassignedTools.length > 0) {
      console.warn(
        `⚠️ ${unassignedTools.length} tools were not assigned to any stage:`,
        unassignedTools.map((t) => `"${t.tool}" (${t.stage_assignment})`),
      );
    }

    return toolsByStage;
  }

  // Get proven tool functions from StrategyWorkflowService
  getProvenToolFunctions() {
    // Import the proven functions - in a real implementation, these would be imported
    // For now, recreate the essential logic
    return {
      filterRelevantTools: (tools, designChallenge, stages, methods) => {
        if (!Array.isArray(tools)) return [];

        const normalizedStages = stages.map((stage) => stage.toLowerCase());
        const normalizedMethods = methods.map((method) => method.toLowerCase());

        return tools.filter((tool) => {
          const toolStage = tool.related_stage
            ? tool.related_stage.toLowerCase()
            : "";
          const stageMatch = normalizedStages.includes(toolStage);

          const toolMethods = tool.related_methods
            ? tool.related_methods
                .split(",")
                .map((method) => method.trim().toLowerCase())
            : [];
          const methodMatch = toolMethods.some((toolMethod) =>
            normalizedMethods.includes(toolMethod),
          );

          return stageMatch && methodMatch;
        });
      },

      calculateProgramLengthAndStages: (relevantTools) => {
        const stages = {};

        relevantTools.forEach((tool) => {
          const stageName = tool.related_stage || "Implementation";
          if (!stages[stageName]) {
            stages[stageName] = { tools: [], totalDuration: 0 };
          }
          stages[stageName].tools.push(tool);
          stages[stageName].totalDuration += parseFloat(tool.duration) || 0;
        });

        return stages;
      },

      determineToolDuration: (tool, complexityScore, urgencyMultiplier) => {
        if (!tool) return 8;

        let lowDuration =
          parseFloat(tool.low_duration) || parseFloat(tool.duration) || 8;
        let highDuration = parseFloat(tool.high_duration) || lowDuration;

        let adjustedDuration = highDuration;

        if (urgencyMultiplier > 1) {
          adjustedDuration /= urgencyMultiplier;
        }

        let complexityAdjustment =
          (highDuration - lowDuration) * complexityScore;
        adjustedDuration -= complexityAdjustment;

        if (adjustedDuration > highDuration) {
          adjustedDuration = highDuration;
        } else if (adjustedDuration < lowDuration) {
          adjustedDuration = lowDuration;
        }

        return Math.max(1, Math.round(adjustedDuration));
      },

      detectComplexity: (designChallenge) => {
        const complexityKeywords = {
          simple: 1,
          basic: 1,
          straightforward: 1,
          moderate: 2,
          intermediate: 2,
          challenging: 3,
          complex: 3,
          complicated: 3,
          advanced: 3,
        };

        for (const [keyword, multiplier] of Object.entries(
          complexityKeywords,
        )) {
          if (designChallenge.toLowerCase().includes(keyword)) {
            return multiplier;
          }
        }

        return 2.0;
      },

      detectUrgency: (designChallenge) => {
        const urgencyKeywords = {
          quick: 1,
          fast: 2,
          urgent: 1,
          asap: 1,
          weeks: 2,
          months: 3,
        };

        for (const [keyword, multiplier] of Object.entries(urgencyKeywords)) {
          if (designChallenge.toLowerCase().includes(keyword)) {
            return multiplier;
          }
        }

        return 1.0;
      },
    };
  }

  // Get design challenge context from workflow results
  getDesignChallengeContext() {
    // Use the original design challenge or create a context from analysis
    return (
      this.workflowResults.deepAnalysis?.problem_statement ||
      this.workflowResults.deepAnalysis?.short_summary ||
      "Strategic design challenge requiring comprehensive approach"
    );
  }

  // Get CSV tools data (this should be injected from the workflow execution)
  getCsvToolsData() {
    // In the real implementation, this would come from the same CSV source as regular workflow
    // For now, return a basic set that would be available
    return this.csvToolsCache || [];
  }

  // REMOVED: createFallbackToolsByStage - no more fake fallback tools

  // Helper to identify how AI assigned tools
  getAssignmentMethod(tool, stageName, programName) {
    if (tool.stage_assignment === stageName) return "exact_stage_match";
    if (tool.stage?.toLowerCase() === stageName.toLowerCase())
      return "stage_field_match";
    if (tool.related_stage === stageName) return "related_stage_match";
    if (tool.context_fit?.toLowerCase().includes(stageName.toLowerCase()))
      return "context_fit_match";
    if (this.fuzzyMatch(tool.stage_assignment, stageName, 0.7))
      return "fuzzy_stage_match";
    if (tool.program_assignment === programName) return "program_match";
    return "unknown";
  }

  // AI-driven stage matching - let the AI determine relationships
  semanticStageMatch(toolStage, programStage) {
    if (!toolStage || !programStage) return false;

    const normalizeStage = (stage) => stage.toLowerCase().trim();
    const tool = normalizeStage(toolStage);
    const program = normalizeStage(programStage);

    // Only use exact matches and partial word matches - let AI handle semantics
    if (tool === program) return true;

    // Check for partial word matches (e.g., "User Research" → "Research")
    const programWords = program.split(" ");
    const toolWords = tool.split(" ");

    return programWords.some((pWord) =>
      toolWords.some(
        (tWord) =>
          pWord.length > 3 &&
          tWord.length > 3 &&
          (pWord.includes(tWord) || tWord.includes(pWord)),
      ),
    );
  }

  // Helper method for fuzzy string matching
  fuzzyMatch(str1, str2, threshold = 0.6) {
    if (!str1 || !str2) return false;

    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();

    // Exact match
    if (s1 === s2) return true;

    // Contains match
    if (s1.includes(s2) || s2.includes(s1)) return true;

    // Simple similarity check
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return true;

    const similarity =
      (longer.length - this.levenshteinDistance(longer, shorter)) /
      longer.length;
    return similarity >= threshold;
  }

  // Simple Levenshtein distance calculation
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  // NO MORE HARDCODED TEMPLATES - AI creates everything autonomously

  // Helper to understand stage progression within the overall solution
  getStageProgression(stageName, stageIndex, totalStages) {
    const progressPercent = ((stageIndex + 1) / totalStages) * 100;

    if (progressPercent <= 25) return "discovery_initiation";
    if (progressPercent <= 50) return "research_analysis";
    if (progressPercent <= 75) return "design_development";
    return "validation_implementation";
  }

  // Parse AI-generated complexity analysis instead of hardcoded mapping
  parseAIComplexity(customTool){
    // First try to get complexity from AI analysis
    if (customTool.complexity_analysis) {
      const analysis = customTool.complexity_analysis.toLowerCase();
      if (
        analysis.includes("simple") ||
        analysis.includes("routine") ||
        analysis.includes("straightforward")
      ) {
        return "simple";
      }
      if (
        analysis.includes("complex") ||
        analysis.includes("advanced") ||
        analysis.includes("sophisticated")
      ) {
        return "complex";
      }
      return "medium"; // Default for anything in between
    }

    // Fallback to duration-based complexity if no analysis
    const duration = customTool.duration || 8;
    if (duration <= 6) return "simple";
    if (duration >= 20) return "complex";
    return "medium";
  }

  // AI generates all stage descriptions - no more hardcoded templates

  // REMOVED: No more CSV-based tool generation for autonomous workflows
  // All tools are now generated by AI in the autonomous endpoints
  // REMOVED: generateContextualFallbackTools - no more fake fallback tools

  // Helper method to extract methods from autonomous program
  extractMethodsFromProgram(program) {
    console.log(`🔍 Extracting methods for program: ${program.program_of_work}`);
    console.log(`🔍 Raw program data:`, {
      methodology: program.methodology,
      methods: program.methods,
      related_methods: program.related_methods,
      programType: typeof program
    });

    // SIMPLIFIED: The AI is providing methodology correctly, just extract it
    // 1. Use methodology field from AI (single string) - this is the main source
    if (program.methodology && typeof program.methodology === 'string') {
      const methodology = program.methodology.trim();
      if (methodology && methodology !== 'null' && methodology !== 'undefined' && methodology !== '') {
        console.log(`✅ Using AI methodology field: "${methodology}"`);
        return [methodology];
      }
    }

    // 2. Use methods array if it exists
    if (Array.isArray(program.methods) && program.methods.length > 0) {
      const validMethods = program.methods.filter(method => 
        method && typeof method === 'string' && method.trim() !== '' && 
        method.trim() !== 'null' && method.trim() !== 'undefined'
      ).map(method => method.trim());
      
      if (validMethods.length > 0) {
        console.log(`✅ Using AI methods array:`, validMethods);
        return validMethods;
      }
    }

    // 3. Use related_methods as backup
    if (Array.isArray(program.related_methods) && program.related_methods.length > 0) {
      const validMethods = program.related_methods.filter(method => 
        method && typeof method === 'string' && method.trim() !== '' && 
        method.trim() !== 'null' && method.trim() !== 'undefined'
      ).map(method => method.trim());
      
      if (validMethods.length > 0) {
        console.log(`✅ Using AI related_methods:`, validMethods);
        return validMethods;
      }
    }

    // If no valid methods found, this should not happen with working AI
    console.error(`❌ AI failed to provide any valid methods for program: ${program.program_of_work}`);
    console.error(`❌ Program data:`, program);
    throw new Error(`Autonomous AI failed to provide methodology for program: ${program.program_of_work}. The AI prompt needs to be fixed.`);
  }

  // Helper method to generate program deliverables using existing AI endpoint
  async generateProgramDeliverables(designChallenge, program, toolsByStage) {
    try {
      const tools = Object.values(toolsByStage).flatMap(stage => 
        stage.tools.map(tool => ({
          name: tool.tool,
          description: tool.description || "",
          duration: tool.duration || 1
        }))
      );

      const response = await fetchWithAuth("/api/ai/generate-expectedDeliverables", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          designChallenge: designChallenge,
          programName: program.program_of_work,
          tools: tools,
          problemStatement: this.workflowResults.deepAnalysis?.problem_statement || "",
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.deliverables || [];
      }
    } catch (error) {
      console.warn("Failed to generate deliverables:", error);
    }
    return [];
  }

  // Helper method to generate program stakeholders using existing AI endpoint  
  async generateProgramStakeholders(designChallenge, program, toolsByStage) {
    try {
      const tools = Object.values(toolsByStage).flatMap(stage => 
        stage.tools.map(tool => ({
          name: tool.tool,
          description: tool.description || "",
          duration: tool.duration || 1
        }))
      );

      const response = await fetchWithAuth("/api/ai/generate-stakeholdersInvolvement", {
        method: "POST", 
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          designChallenge: designChallenge,
          programName: program.program_of_work,
          tools: tools,
          problemStatement: this.workflowResults.deepAnalysis?.problem_statement || "",
          duration: program.duration_estimate || 40
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.stakeholders || [];
      }
    } catch (error) {
      console.warn("Failed to generate stakeholders:", error);
    }
    return [];
  }

  reset() {
    this.workflowResults = {};
    this.conversationHistory = [];
  }

  addConversationMessage(message) {
    this.conversationHistory.push({
      ...message,
      timestamp: new Date().toISOString(),
    });
  }
}

export default AutonomousStrategyWorkflowService;